import { Box, Button, Flex, Skeleton, Text } from "@chakra-ui/react";
import { ETH, RSETH, WRSETH } from "@config/contractsConfig";
import {
  CONNECT_WALLET_CLICK,
  VAULT_DEPOSIT_CLICK,
  VAULT_MAX_DEPOSIT_CLICK,
} from "@constants/analytics";
import {
  toFixedWithoutRounding,
  MAX_FRACTION_DIGITS,
  CONNECT_WALLET_CTA,
  SWITCH_NETWORK,
  DEPOSIT_NOW,
  APPROVE_CTA,
  isNumber,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappConfig from "@hooks/useDappConfig";
import useTokenBalance from "@hooks/useTokenBalance";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isFloat,
  toStringPrecision,
  trackEvent,
  emitReduxEvent,
  formatNumber,
} from "utils";
import { useAccount } from "wagmi";
import YieldTicketInput from "../../VaultYield/DepositWithdraw/YieldTicketInput";
import YieldTicketInputBalance from "../../VaultYield/DepositWithdraw/YieldTicketInputBalance";
import YieldTokenSelect from "../../VaultYield/DepositWithdraw/YieldTokenSelect";
import useExchangeRate from "@hooks/useExchangeRate";
import useVaultAllowance from "@hooks/useVaultAllowance";
import useAgEthExchangeRate from "@hooks/useAgEthExchangeRate";
import { updateUserData } from "store";

const YieldDeposit = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();

  const { isVaultSupportedChain, config, vaultConfig } = useDappConfig();

  const { erc20Allowance } = useVaultAllowance();

  const {
    tokensBalances,
    xTokenVaultBalance,
    xTokenTokenBalance,
    nativeTokenBalance,
  } = useTokenBalance();

  const { selectedERC20Vault, referralId } = useSelector(
    (state: any) => state.user
  );
  const dispatch = useDispatch();

  const tokenBalance = useMemo(() => {
    if (selectedERC20Vault === config.xtoken.symbol) return xTokenTokenBalance;
    else if (selectedERC20Vault === ETH) return nativeTokenBalance;
    return tokensBalances[selectedERC20Vault];
  }, [xTokenVaultBalance, tokensBalances, selectedERC20Vault]);

  const { openConnectModal } = useConnectModal();

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't deposit more than available balance in your wallet.`;
      }
      return error;
    },
    [isConnected, tokenBalance, selectedERC20Vault, amount, tokensBalances]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  const isApproved = useMemo(() => {
    if (selectedERC20Vault === ETH) return true;
    return (
      erc20Allowance &&
      Number(erc20Allowance) &&
      Number(erc20Allowance) >= Number(amount)
    );
  }, [erc20Allowance, amount, selectedERC20Vault]);

  const conversionsDataAgEth = useAgEthExchangeRate();
  const conversionDataRsEth = useExchangeRate("vault");

  const conversionsRsETH = useMemo(() => {
    const conversionToERC20 = conversionDataRsEth?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionDataRsEth?.exchangeRate]);

  const getAgEthExchange = useCallback(() => {
    const rsETHRate =
      selectedERC20Vault === RSETH || selectedERC20Vault === WRSETH
        ? 1
        : Number(conversionsRsETH?.toERC20);
    return Number(conversionsDataAgEth?.toERC20) * rsETHRate;
  }, [
    conversionsDataAgEth?.toERC20,
    conversionsRsETH?.toERC20,
    selectedERC20Vault,
  ]);

  const convertedAmount = useMemo(() => {
    const agRate = getAgEthExchange();
    if (amount && isNumber(amount)) {
      const value = Number(amount) / (agRate > 0 ? agRate : 1);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, getAgEthExchange]);

  const BUTTON_TEXT = useMemo(() => {
    if (!isConnected) return CONNECT_WALLET_CTA;
    else if (!isVaultSupportedChain) return SWITCH_NETWORK;
    else {
      if (isApproved) return DEPOSIT_NOW;
      return APPROVE_CTA;
    }
  }, [isConnected, isApproved, isVaultSupportedChain]);

  const handleClick = useCallback(() => {
    if (!isConnected) {
      openConnectModal?.();
      trackEvent(CONNECT_WALLET_CLICK, {
        cta_location: "vault_deposit_cta",
        cta: CONNECT_WALLET_CTA,
      });
    } else if (!isVaultSupportedChain) {
      emitReduxEvent(EVENTS.SWITCH_NETWORK);
    } else {
      if (isApproved) {
        if (!amount) {
          setError(
            "This field can't be blank or zero. Please provide valid input."
          );
          return;
        }

        trackEvent(VAULT_DEPOSIT_CLICK, { amount, asset: selectedERC20Vault });

        emitReduxEvent(EVENTS.HANDLE_VAULT_DEPOSIT, {
          amount: amount,
          referralId,
        });

        setAmount("");
      } else {
        emitReduxEvent(EVENTS.HANDLE_VAULT_APPROVE, { amount: amount });
      }
    }
  }, [isConnected, isApproved, amount, openConnectModal]);

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    trackEvent(VAULT_MAX_DEPOSIT_CLICK);
    setAmount(toFixedWithoutRounding(tokenBalance.toString(), 4));
  }, [tokenBalance]);

  const handleTokenSelect = (key: string) => {
    dispatch(
      updateUserData({
        ["selectedERC20Vault"]: key,
      })
    );
  };

  const insufficientFund = false;

  return (
    <Flex
      fontSize="14px"
      lineHeight="20px"
      fontWeight="400"
      pt="20px"
      direction={"column"}
    >
      <Flex
        justifyContent={"space-between"}
        mx="16px"
        mb="15px"
        pt={{ base: "10px", md: "20px" }}
        px={{ base: "8px", md: "16px" }}
        pb={{ base: "10px", md: insufficientFund ? "0px" : "20px" }}
        gap={{ base: "20px", md: "12px" }}
        direction={{ base: "column", md: "row" }}
      >
        <Flex
          width={{ base: "100%", md: "38%" }}
          minW={{ base: "100%", md: "251px" }}
          direction={"column"}
        >
          <Box h="12px">
            <Text fontSize="12px" lineHeight="14.4px" fontWeight="400">
              You deposit
            </Text>
          </Box>

          <Flex>
            <YieldTicketInput
              inputRightElement={
                <YieldTokenSelect
                  selected={selectedERC20Vault}
                  onTokenSelect={handleTokenSelect}
                />
              }
              value={amount}
              onChange={(value: string) => handleChange(value)}
              error={error}
            />
          </Flex>

          <Flex mt="8px">
            <YieldTicketInputBalance
              label="Available"
              tokenAmount={tokenBalance}
              onClick={handleMaxClick}
              isConnected={isConnected}
              token={selectedERC20Vault}
            />
          </Flex>
        </Flex>

        <Flex
          direction={"column"}
          width={{ base: "100%", md: "38%" }}
          minW={{ base: "100%", md: "251px" }}
        >
          <Box h="12px">
            <Text fontSize="12px" lineHeight="14.4px" fontWeight="400">
              You get
            </Text>
          </Box>

          <Flex>
            <YieldTicketInput
              inputRightElement={
                <YieldTokenSelect
                  disabled={true}
                  selected={vaultConfig.xToken.symbol}
                  tokenBgColor="#ECECEC"
                />
              }
              value={
                formatNumber(
                  Number(convertedAmount),
                  false,
                  MAX_FRACTION_DIGITS,
                  true
                ) || "0"
              }
              error={error}
              disabled={true}
            />
          </Flex>
          <Flex mt="8px">
            <YieldTicketInputBalance
              label="Balance"
              tokenAmount={xTokenVaultBalance}
              token={vaultConfig.xToken.symbol}
              onClick={() => {}}
              isConnected={isConnected}
              hideMax
            />
          </Flex>
        </Flex>

        <Flex alignItems={"center"} justifyContent={"center"}>
          <Button
            borderRadius="12px"
            bg="textPrimary"
            p="8px 16px"
            h="48px"
            mt={{ base: 0, md: isConnected ? "-10px" : "12px" }}
            width="100%"
            _hover={{
              bg: "textPrimary",
            }}
            onClick={handleClick}
            isDisabled={isConnected && (error !== "" || amount === "")}
          >
            <Text
              fontSize="16px"
              lineHeight="24px"
              fontWeight="600"
              color="backgroundCard"
            >
              {BUTTON_TEXT}
            </Text>
          </Button>
        </Flex>
      </Flex>

      {!insufficientFund && error && (
        <Box
          bgColor="#FF616A"
          p="12px 24px"
          borderRadius={"6px"}
          m={{ base: "0 24px 24px 24px", md: "0 32px 24px 32px" }}
        >
          <Text
            color="white"
            fontSize={"13px"}
            fontWeight={"500"}
            lineHeight={"15.6px"}
          >
            {error}
          </Text>
        </Box>
      )}

      {insufficientFund && (
        <Box
          bgColor="#FF616A"
          p="12px 24px"
          borderRadius={"6px"}
          m={{ base: "0 24px 24px 24px", md: "0 32px 24px 32px" }}
        >
          <Text
            color="white"
            fontSize={"13px"}
            fontWeight={"500"}
            lineHeight={"15.6px"}
          >
            Insufficient Balance. Please try again
          </Text>
        </Box>
      )}

      <Box
        w={"100%"}
        borderTop="1px solid"
        borderColor="gray.200"
        pb={2}
        px="30px"
      >
        <Flex
          gap={{ base: 0, md: "10px" }}
          direction={{ base: "column", md: "row" }}
        >
          <Flex mt="1rem" gap="4px">
            <Text fontSize={"12px"} fontWeight={"400"} color="#A1B6BA">
              Exchange rate
            </Text>
            {conversionsDataAgEth.isLoading ? (
              <Skeleton height="20px" w="120px" />
            ) : (
              <>
                {conversionsDataAgEth.toERC20 ? (
                  <Text fontSize={"12px"} fontWeight={"600"} color="#3F6870">
                    1 {selectedERC20Vault} ={" "}
                    {getAgEthExchange() > 0
                      ? toFixedWithoutRounding(
                          (getAgEthExchange() > 0
                            ? 1 / getAgEthExchange()
                            : 0
                          )?.toString(),
                          4
                        )
                      : "-"}{" "}
                    {vaultConfig?.xToken?.symbol}
                  </Text>
                ) : null}
              </>
            )}
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

export default YieldDeposit;
